"use client";

import { ArrowRight, Eye, Globe, Lock, ScrollText, SlidersHorizontal, UserRoundCog } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Advisory() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    index: "01",
    title: "Real-time financial visibility",
    description: "Move beyond annual filings with dynamic dashboards that track your cash flow and operational health daily.",
    Icon: ScrollText
  }, {
    index: "02",
    title: "Strategic tax architecture",
    description: "Proactive planning that aligns your corporate structure with long-term growth and wealth preservation goals.",
    Icon: SlidersHorizontal
  }, {
    index: "03",
    title: "Operational workflow optimization",
    description: "We refine your internal processes, from inventory management to fleet expenses, to maximize your margins.",
    Icon: UserRoundCog
  }, {
    index: "04",
    title: "Industry-specific benchmarking",
    description: "Leverage data-driven insights tailored to today’s unique trades, retail, and consulting landscapes.",
    Icon: Globe
  }, {
    index: "05",
    title: "Scalable advisory partnership",
    description: "A dedicated advisory team that evolves with your business, ensuring you remain scale-ready at every stage.",
    Icon: Lock
  }];
  return <section data-section-id="15923" id="the-advisory-advantage" className="dark bg-background text-foreground py-32 md:py-44">
      <div className="mx-auto max-w-[1280px] px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-center gap-3 mb-8">
                <span className="font-default text-xs font-medium uppercase" style={{
                color: "var(--primary)",
                letterSpacing: "0.14em"
              }}>
                  Why ScaleBase Advisory
                </span>
              </div>

              <h2 className="font-default font-normal text-foreground leading-[1.1] tracking-tight" style={{
              fontSize: "clamp(2rem, 3.4vw, 3.25rem)"
            }}>
                The advisory edge,{" "}
                <em className="italic relative inline-block" style={{
                color: "var(--primary)"
              }}>
                  engineered.
                </em>
              </h2>

              <p className="font-default mt-8 text-foreground/75 leading-[1.6]" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
              maxWidth: "40ch"
            }}>Five core pillars that transform your accounting from a compliance burden into a strategic engine for business growth.</p>

              <div className="mt-10">
                <Link to="#scale-ready-assessment">
                  
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7">
            <ul className="flex flex-col">
              {benefits.map((benefit, i) => {
              const Icon = benefit.Icon;
              return <li data-index={i} key={benefit.index} className={`group border-t border-[color:var(--border)] hover:border-t-[color:var(--primary)] transition-colors duration-300 ease-out py-10 ${i === benefits.length - 1 ? "border-b border-b-[color:var(--border)] hover:border-b-[color:var(--primary)]" : ""} ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all`} style={{
                transitionDuration: "700ms",
                transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                transitionDelay: rightVisible ? `${i * 80}ms` : "0ms"
              }}>
                    <div className="grid grid-cols-12 gap-4 items-start">
                      {}
                      <div className="col-span-2">
                        <span className="font-mono font-medium text-sm tracking-[0.04em]" style={{
                      color: "var(--primary)"
                    }}>
                          {benefit.index}
                        </span>
                      </div>

                      {}
                      <div className="col-span-8">
                        <h3 className="font-default font-medium text-foreground leading-[1.25] tracking-tight" style={{
                      fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)"
                    }}>
                          {benefit.title}
                        </h3>
                        <p className="font-default mt-3 text-sm leading-[1.6] text-foreground/70">
                          {benefit.description}
                        </p>
                      </div>

                      {}
                      <div className="col-span-2 flex justify-end">
                        <Icon className="h-6 w-6 text-foreground/60 group-hover:text-foreground transition-opacity duration-300 ease-out" strokeWidth={1.25} />
                      </div>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}